import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMapPin, FiClock, FiUsers, FiWifi } = FiIcons;

const About = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const stats = [
    { icon: FiMapPin, number: '10', label: 'Acres of Privacy' },
    { icon: FiClock, number: '18°C', label: 'Average Temperature' },
    { icon: FiUsers, number: '8', label: 'Guest Capacity' },
    { icon: FiWifi, number: '100%', label: 'Digital Coverage' },
  ];

  return (
    <section className="py-20 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white font-serif mb-6">
            The Ullinduwawa Experience
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Discover the Sri Lanka you haven't seen yet. Nestled between the Rakwana-Deniyaya hills 
            and the pristine Sinharaja Forest Reserve, our estate offers the perfect escape from 
            the ordinary.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-8"
          >
            <div className="glass-card p-8 rounded-2xl">
              <h3 className="text-2xl font-bold text-white mb-4 font-serif">
                A Heritage of Hospitality
              </h3>
              <p className="text-gray-300 leading-relaxed mb-6">
                Originally established as part of the Akkara Dahaya tea estate, our bungalow 
                has been thoughtfully transformed into a premium retreat while preserving its 
                colonial charm and connection to Sri Lanka's tea heritage.
              </p>
              <p className="text-gray-300 leading-relaxed">
                The journey to reach us via the scenic Rakwana-Deniyaya road is an adventure 
                in itself, featuring the famous Bulutota Pass with panoramic views that will 
                take your breath away.
              </p>
            </div>

            <div className="glass-card p-8 rounded-2xl">
              <h3 className="text-2xl font-bold text-white mb-4 font-serif">
                The Goldilocks Climate
              </h3>
              <p className="text-gray-300 leading-relaxed">
                Unlike the humid coastal areas or the often misty central highlands, 
                Ullinduwawa enjoys a perfect climate - cool nights for restful sleep 
                and warm, clear days ideal for exploration and photography.
              </p>
            </div>
          </motion.div>

          {/* Stats Grid */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="grid grid-cols-2 gap-6"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.05, y: -5 }}
                className="glass-card p-6 rounded-2xl text-center"
              >
                <div className="w-16 h-16 mx-auto mb-4 glass-morphism rounded-full flex items-center justify-center">
                  <SafeIcon icon={stat.icon} className="w-8 h-8 text-emerald-300" />
                </div>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={inView ? { scale: 1 } : {}}
                  transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                  className="text-3xl font-bold text-white mb-2"
                >
                  {stat.number}
                </motion.div>
                <p className="text-gray-300 text-sm">{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Location Highlights */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-20"
        >
          <div className="glass-card p-8 rounded-2xl">
            <h3 className="text-3xl font-bold text-white mb-8 text-center font-serif">
              Gateway to Natural Wonders
            </h3>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-4 glass-morphism rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiMapPin} className="w-10 h-10 text-emerald-300" />
                </div>
                <h4 className="text-xl font-semibold text-white mb-2">Gongala Mountain</h4>
                <p className="text-gray-300 text-sm">
                  15th highest peak in Sri Lanka with misty pygmy forests and endemic biodiversity
                </p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-4 glass-morphism rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiMapPin} className="w-10 h-10 text-emerald-300" />
                </div>
                <h4 className="text-xl font-semibold text-white mb-2">Patna Sliding Rock</h4>
                <p className="text-gray-300 text-sm">
                  Natural water slide over smooth rock formations into crystal-clear pools
                </p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-4 glass-morphism rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiMapPin} className="w-10 h-10 text-emerald-300" />
                </div>
                <h4 className="text-xl font-semibold text-white mb-2">Sinharaja Reserve</h4>
                <p className="text-gray-300 text-sm">
                  UNESCO World Heritage rainforest with rare birds and pristine wilderness
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;